import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { motion } from "framer-motion";
import { storyChangeAnim } from "../data/common";

// Import Swiper React components
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/bundle";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import "swiper/css/navigation";

// import required modules
import { EffectFade, EffectCoverflow, Pagination } from "swiper/modules";

/* comp imports */
import SwiperBlock from "./SwiperBlock";
import SwiperVideoBlock from "./SwiperVideoBlock";

const SwiperWrapper = forwardRef(({
  chapterData,
  storyHeadText,
  animationType,
  currStoryId,
  totalNumStories,
  storyChangeHandler,
  currentIndex,
  setCurrentIndex,
  setTotalChapters,
  fromFirstSlide,
  setFromFirstSlide,
  fromEndScreen,
  setFromEndScreen,
  setShowEndScreen,
  isMobile,
  setStoryHead,
  setChapterHashTags,
  callGaHandler,
  changeAnim,
  isLandscape,
  categoryListData,
  showFeedbackScreen,
  onShowFeedback,
}, ref) => {
  const swiperRef = useRef(null);
  const shareRef = useRef(null);
  let timer = useRef(null);
  let interval = useRef(null);
  let newIndex = useRef(null);
  let progress = useRef(1);

  /* use states */

  const [isSlidePaused, setIsSlidePaused] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [firstRender, setFirstRender] = useState(true);
  const [prevSlideTouchDiff, setPrevSlideTouchDiff] = useState(0);
  let isNavigationClicked = false;

  const swipeDirection = (prevSlideTouchDiff, slideTouchDiff) => {
    if (!isNavigationClicked && (prevSlideTouchDiff === slideTouchDiff || slideTouchDiff === 0)) {
      return callGaHandler('Story_swipe', 'Auto');
    }

    if (slideTouchDiff > 0) {
      return callGaHandler('Story_swipe', 'Manual');
    }

    if (slideTouchDiff < 0) {
      return callGaHandler('Story_swipe', 'Manual');
    }
  }

  const changeCurrentIndexHandler = (s) => {
    swipeDirection(prevSlideTouchDiff, s?.touches?.diff);
    setPrevSlideTouchDiff(() => s?.touches?.diff);
    const index = s.activeIndex;
    setCurrentIndex(index);
    setChapterHashTags(chapterData[index]?.chapter_hash_tags);
    setIsSlidePaused(false);
    progress.current = 1;
    setCurrentProgress(0);
    isNavigationClicked = false;
  };

  // useEffect(() => {
  //   let _stories = chapterData;
  //   if (chapterData && chapterData?.length > 0) {
  //     // const prevStoryId = Number(currStoryId) - 1;
  //     // const prevChapters = categoryListData.filter(category => category.category_id === prevStoryId)[0]?.chapters || [];
  //     // if (prevChapters.length > 0) {
  //     //   _stories = [prevChapters[prevChapters.length - 1], ..._stories];
  //     //   setStoryStatus(prev => ({ ...prev, isPrev: true }));
  //     // }
  //     // const nextStoryId = Number(currStoryId) + 1;
  //     // const nextChapters = categoryListData.filter(category => category.category_id === nextStoryId)[0]?.chapters || [];
  //     // if (nextChapters.length > 0) {
  //     //   _stories = [..._stories, nextChapters[0]];
  //     //   setStoryStatus(prev => ({ ...prev, isNext: true }));
  //     // }
  //   }
  //   const debounce = setTimeout(() => {
  //     setStories(_stories);
  //   }, 200);

  //   return () => clearTimeout(debounce);
  // }, [chapterData]);

  useEffect(() => {
    if (firstRender) setFirstRender(false);
    else pauseSlideOnTap();
  }, [isLandscape]);

  useEffect(() => {
    if (storyHeadText) {
      setStoryHead(storyHeadText);
      setChapterHashTags(chapterData[0]?.chapter_hash_tags);
    }
  }, [storyHeadText]);

  const pauseSlideOnTap = (sendGAEvent = false, value = '') => {
    if (sendGAEvent && !isSlidePaused) {
      callGaHandler('Pause', chapterData[currentIndex].chapter_name);
    } else if (sendGAEvent && isSlidePaused) {
      shareRef.current?.closeShareOption();
      callGaHandler('Resume', chapterData[currentIndex].chapter_name);
    }
    switch (value) {
      case 'pause':
        setIsSlidePaused(true);
        break;

      case 'play':
        setIsSlidePaused(false);
        break;
    
      default:
        setIsSlidePaused(!isSlidePaused);
        break;
    }
    setCurrentProgress(progress.current);
  };

  useEffect(() => {
    newIndex.current = currentIndex;
    clearTimeout(timer.current);
    clearInterval(interval.current);
    setTotalChapters(chapterData.length);

    if (isSlidePaused) {
      progress.current = currentProgress;
    } else {
      if (currentProgress) {
        progress.current = currentProgress;
      } else {
        progress.current = 1;
      }
    }

    const autoplayDelay = (index) => {
      if (fromFirstSlide || fromEndScreen) {
        swiperRef.current.swiper.slideTo(index);
        setFromFirstSlide(false);
        setFromEndScreen(false);
      }
      if (swiperRef.current) {
        if (!isSlidePaused) {
          timer.current = setTimeout(() => {
            if (index < chapterData.length - 1) {
              let newIndex = index + 1;
              swiperRef.current.swiper.slideTo(newIndex);
              setCurrentIndex(newIndex);
            } else {
              handleSwipeOnLastSlide();
            }
          }, (chapterData[index].chapter_delay * (100 - progress.current)) / 100);
        }

        if (!isSlidePaused) {
          interval.current = setInterval(() => {
            if (index < chapterData.length) {
              progress.current += 1;

              let activePaginationElem = document.querySelector(
                ".swiper-pagination-bullet-active"
              );

              activePaginationElem.style.setProperty(
                "--progress",
                progress.current / 100
              );
            }
          }, chapterData[index].chapter_delay / 100);
        }
      }
      // callGaHandler("Swipe", "Auto");
    };

    autoplayDelay(currentIndex);

    return () => {
      clearTimeout(timer.current);
      clearInterval(interval.current);
    };
  }, [currentIndex, chapterData, isSlidePaused]);

  useEffect(() => {
    return () => {
      clearTimeout(timer.current);
      clearInterval(interval.current);
    };
  }, [chapterData]);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.swiper.on("touchEnd", () => {
        if (
          swiperRef.current.swiper.isEnd &&
          swiperRef.current.swiper.swipeDirection === "next"
        ) {
          handleSwipeOnLastSlide();
        } else if (
          swiperRef.current.swiper.isBeginning &&
          swiperRef.current.swiper.swipeDirection === "prev"
        ) {
          handleSwipeOnFirstSlide();
        }
      });
    }
  }, []);

  useEffect(() => {
    const apiCall = setTimeout(() => {
      callGaHandler("Chapter", `chapter_${currentIndex + 1}`);
      callGaHandler(
        `${chapterData[currentIndex]?.chapter_name}`,
        `chapter_${currentIndex + 1}`
      );
    }, 100);

    return () => clearTimeout(apiCall);
  }, [currentIndex]);

  const handleSwipeOnLastSlide = () => {
    clearInterval(interval.current);
    if (Number(currStoryId) !== totalNumStories - 1) {
      storyChangeHandler(Number(currStoryId) + 1, false);
    } else {
      // setShowEndScreen(true);
      onShowFeedback();
    }
  };

  const handleSwipeOnFirstSlide = () => {
    clearInterval(interval.current);
    if (Number(currStoryId) !== 1) {
      const prevStoryId = Number(currStoryId) - 1;
      const prevChapters = categoryListData.filter(category => category.category_id === prevStoryId)[0]?.chapters || [];
      storyChangeHandler(prevStoryId, true);
      setCurrentIndex(prevChapters.length - 1);
    }
  };

  const moveToPrev = () => {
    isNavigationClicked = true;
    if (currentIndex === 0 && Number(currStoryId) === 1) {
      handleSwipeOnFirstSlide();
    } else {
      if (currentIndex === 0) {
        handleSwipeOnFirstSlide();
      } else {
        swiperRef.current.swiper.slidePrev();
      }
    }
    callGaHandler('Story_swipe', 'Manual');
  };

  const moveToNext = () => {
    isNavigationClicked = true;
    let index = currentIndex;
    // if (storyStatus.isNext) {
    //   index = currentIndex + 1;
    // }
    if (index < chapterData.length - 1) {
      swiperRef.current.swiper.slideNext();
    } else {
      handleSwipeOnLastSlide();
    }
    callGaHandler('Story_swipe', 'Manual');
  };

  useImperativeHandle(ref, () => {
    return {
      pauseSlideOnTap,
    }
  });

  return (
    <motion.div
      key={currStoryId}
      className="main-swiper-wrapper"
      variants={storyChangeAnim}
      initial="initial"
      animate="active"
      exit="exit"
    >
      <Swiper
        effect={!isMobile ? "coverflow" : animationType === "fade" ? "fade" : ""}
        spaceBetween={0}
        centeredSlides={true}
        slidesPerView={
          isMobile ? 1 : 3
        }
        onSlideChange={(s) => changeCurrentIndexHandler(s)}
        autoplay={false}
        coverflowEffect={{
          rotate: -30,
          stretch: -20,
          depth: 120,
          modifier: 1,
          slideShadows: true,
        }}
        pagination={{
          clickable: true,
        }}
        modules={[EffectFade, EffectCoverflow, Pagination]}
        loop={false}
        ref={swiperRef}
      >
        {chapterData &&
          chapterData.length &&
          chapterData.map((chapterInfo, index) =>
            chapterInfo.chapter_type === "image" ? (
              <SwiperSlide key={index} className="swiper-slider-eod">
                <SwiperBlock
                  chapterInfo={chapterInfo}
                  pauseSlideOnTap={pauseSlideOnTap}
                  callGaHandler={callGaHandler}
                  showFeedbackScreen={showFeedbackScreen}
                  shareRef={shareRef}
                />
              </SwiperSlide>
            ) : chapterInfo.chapter_type === "video" ? (
              <SwiperSlide key={index} className="swiper-slider-eod">
                {({ isActive }) => (
                  <SwiperVideoBlock
                    isActive={isActive}
                    chapterInfo={chapterInfo}
                    pauseSlideOnTap={pauseSlideOnTap}
                    isSlidePaused={isSlidePaused}
                    progress={currentProgress / 100}
                    callGaHandler={callGaHandler}
                    showFeedbackScreen={showFeedbackScreen}
                    shareRef={shareRef}
                  />
                )}
              </SwiperSlide>
            ) : null
          )}
      </Swiper>

      <div className="swiper-navigation-wrapper d-none d-xl-block">
        <div className="swiper-nav-icons">
          <div
            className="swiper-swiper-nav-icon left-icon"
            onClick={moveToPrev}
          ></div>
          <div
            className="swiper-swiper-nav-icon right-icon"
            onClick={moveToNext}
          ></div>
        </div>
      </div>

    </motion.div>
  );
});

export default SwiperWrapper;
